import { createSelector } from '@reduxjs/toolkit'
import { createSelectorCreator, defaultMemoize } from 'reselect'
import { HighlightWordRange, Transcript } from '.'
import { ID } from '../../utils/id'
import { State } from '../index'

// By default results are not cached which causes multiple rerenders and reduces performance
const createParagraphSelector = createSelectorCreator(
  defaultMemoize,
  {
    maxSize: 1000,
  },
)

const paragraphs = (state: State, props: { transcriptId: ID }) =>
  state
    .transcript
    .transcripts[props.transcriptId]
    .paragraphIds
    .map((id) => state.transcript.paragraphs[id])

const transcript = (state: State, props: { transcriptId: ID | undefined }): Transcript | undefined =>
  props.transcriptId === undefined ? undefined : state.transcript.transcripts[props.transcriptId]

const paragraphIds = (state: State, props: { transcriptId: ID | undefined }) => {
  const tmp = transcript(state, props)

  if (!tmp) {
    return [] as ID[]
  }

  return tmp.paragraphIds
}

const speakerIds = (state: State, props: { transcriptId: ID }) => {
  const tmp = transcript(state, props)

  if (!tmp) {
    return [] as ID[]
  }

  return tmp.speakerIds
}

const tagIds = (state: State, props: { transcriptId: ID }) => {
  const tmp = transcript(state, props)

  if (!tmp) {
    return [] as ID[]
  }

  return tmp.tagIds
}

export const getParagraphText = (state: State, props: { paragraphId: ID }) => {
  return state.transcript.paragraphs[props.paragraphId]?.text ?? ''
}

export const getParagraphIds = createSelector(
  [
    paragraphIds,
    (state: State) => state.transcript.paragraphs,
  ],
  (ids, paragraphs) => {
    return ids.slice().sort(
      (a, b) => paragraphs[a].startTime - paragraphs[b].startTime,
    )
  },
)

export const getFirstParagraphId = createSelector(
  [getParagraphIds],
  (paragraphs) => paragraphs.length > 0 ? paragraphs[0] : null,
)

export const getLastParagraphId = createSelector(
  [getParagraphIds],
  (paragraphs) => paragraphs.length > 0 ? paragraphs[paragraphs.length - 1] : null,
)

export const getParagraphs = createSelector(
  [getParagraphIds, (state: State) => state.transcript.paragraphs],
  (ids, paragraphs) => ids.map(id => paragraphs[id]),
)

export const getVisibileParagraphs = createSelector(
  [
    (state: State) => state.transcript.visibleParagraphs,
    (state: State) => state.transcript.paragraphs,
  ],
  (ids, paragraphs) => ids.slice().sort((a, b) => {
    return paragraphs[a].startTime - paragraphs[b].startTime
  }),
)

export const getSpeakerIds = createSelector([speakerIds], (speakers) => speakers)
export const getSpeakers = createSelector(
  [speakerIds, (state: State) => state.transcript.speakers],
  (ids, speakers) => ids.map(id => speakers[id]),
)
export const getTagIds = createSelector([tagIds], (tags) => tags)
export const getTags = createSelector(
  [tagIds, (state: State) => state.transcript.tags],
  (ids, tags) => {
    return ids.map(id => tags[id])
  },
)

export const getSpeakerTagIds = createSelector([paragraphs], (paragraphs) => {
  const speakerTagIds: { [key: ID]: ID[] } = {}

  paragraphs.forEach((paragraph) => {
    if (!speakerTagIds[paragraph.speakerId]) {
      speakerTagIds[paragraph.speakerId] = []
    }

    paragraph.tagIds.forEach((tag) => {
      if (!speakerTagIds[paragraph.speakerId].includes(tag)) {
        speakerTagIds[paragraph.speakerId].push(tag)
      }
    })
  })

  return speakerTagIds
})

export const getParagraphWords = createParagraphSelector(
  [
    getParagraphText,
    (state, props: { paragraphId: ID }) => props.paragraphId,
  ],
  (completeParagraphText, paragraphId) => {
    if(!paragraphId) {
      return []
    }

    return completeParagraphText?.split(' ')
  })


export const getHighlights = createParagraphSelector(
  (state: State, props: { paragraphId: ID }) => state.transcript.highlights[props.paragraphId],
  (highlighted) => highlighted || [] as HighlightWordRange[],
)

export const getParagraphWordCount = createParagraphSelector([getParagraphWords], (words) => {
  return Number(words.length)
})

export const getParagraphConfidenceCount = createParagraphSelector(
  [
    (state: State, props: { paragraphId: ID }) => (
      state.transcript.paragraphs[props.paragraphId]?.confidences
    ),
  ],
  (confidences) => {
    return (confidences ?? []).reduce((result, c) => {
      if (c > 0.99) {
        result.very++
        return result
      }
      if (c > 0.95) {
        result.fairly++
        return result
      }
      if (c > 0.9) {
        result.pretty++
        return result
      }
      result.slightly++
      return result
    }, {
      fairly: 0,
      pretty: 0,
      slightly: 0,
      very: 0,
    })
  },
)

const internalGetParagraphSentences = createParagraphSelector(
  [getParagraphText],
  (text) => {
    return text.replace(/[^!?.]/g, '')
  },
)

export const getParagraphQuestionCount = createParagraphSelector(
  [internalGetParagraphSentences],
  (sentences) => {
    return sentences.replace(/[^?]/g, '').length
  },
)

export const getParagraphSentenceCount = createParagraphSelector(
  [internalGetParagraphSentences],
  (sentences) => {
    return sentences.length
  },
)

const internalWordCount = (state: State, props: { transcriptId: ID }) => {
  return paragraphIds(state, props)
    .map((paragraphId) => getParagraphWordCount(state, { paragraphId }))
}

export const getWordCount = createParagraphSelector([internalWordCount], (words) => {
  return words.reduce((a: number, b: number) => a + b, 0)
})

const internalQuestionCount = (state: State, props: { transcriptId: ID }) => {
  return paragraphIds(state, props)
    .map((paragraphId) => getParagraphQuestionCount(state, { paragraphId }))
}

export const getQuestionCount = createParagraphSelector([internalQuestionCount], (questions) => {
  return questions.reduce((a: number, b: number) => a + b, 0)
})

const internalSentenceCount = (state: State, props: { transcriptId: ID }) => {
  return paragraphIds(state, props)
    .map((paragraphId) => getParagraphSentenceCount(state, { paragraphId }))
}

export const getSentenceCount = createParagraphSelector([internalSentenceCount], (words) => {
  return words.reduce((a: number, b: number) => a + b, 0)
})

const internalConfidenceCount = (state: State, props: { transcriptId: ID }) => {
  return paragraphIds(state, props)
    .map((paragraphId) => getParagraphConfidenceCount(state, { paragraphId }))
}

export interface ConfidenceCount {
  very: number
  pretty: number
  fairly: number
  slightly: number
}

export const getConfidenceCount = createSelector([internalConfidenceCount], (counts) => {
  return counts.reduce(
    (prev: ConfidenceCount, next: ConfidenceCount) => ({
      fairly: prev.fairly + next.fairly,
      pretty: prev.pretty + next.pretty,
      slightly: prev.slightly + next.slightly,
      very: prev.very + next.very,
    }),
    {
      fairly: 0,
      pretty: 0,
      slightly: 0,
      very: 0,
    },
  )
})

export const getWordConfidence = createParagraphSelector(
  (state: State, props: { paragraphId: ID; index: number }) => {
    return state.transcript.paragraphs[props.paragraphId]?.confidences?.[props.index]
  },
  (confidence) => confidence,
)

export const getAlternatives = createParagraphSelector(
  (state: State, props: { paragraphId: ID; startIndex: number; endIndex: number }) => {
    return state.transcript.paragraphs[props.paragraphId]?.alternatives.filter(
      (alt) => alt.startIndex <= props.startIndex && alt.endIndex >= props.endIndex,
    )
  },
  (alternatives) => alternatives,
)
